import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BtwButtonModule } from '@between/ng-cdk/button';
import { BtwFileUploaderComponent, BtwFileUploaderModule } from '@between/ng-cdk/file-uploader';
import { BtwToastModule, BtwToastService } from '@between/ng-cdk/toast';
import { BtwTextBoxModule } from '@between/ng-cdk/text-box';
import { BtwValidatorModule } from '@between/ng-cdk/validator';
import { BtwDialogModule, BtwDialogRef, DIALOG_DATA } from '@between/ng-cdk/dialog';
import { Guid } from 'guid-typescript';
import { BtwLoadPanelModule } from '@between/ng-cdk/load-panel';
import { BankStatementUploadApiService } from '../api-services/bank-statement-upload-api/bank-statement-upload-api.service';
import { HttpClientModule } from '@angular/common/http';
import { map, switchMap, tap } from 'rxjs';

export interface IBankStatementUploadForm {
  companyName: FormControl<string | null>;
}

@Component({
  selector: 'app-bank-statements-uploader',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    BtwToastModule, 
    BtwTextBoxModule, 
    BtwValidatorModule, 
    BtwDialogModule, 
    BtwFileUploaderModule, 
    BtwButtonModule, 
    BtwLoadPanelModule,
    HttpClientModule
  ],
  providers: [BtwToastService, BankStatementUploadApiService],
  templateUrl: './bank-statements-uploader.component.html',
  styleUrl: './bank-statements-uploader.component.scss'
})
export class BankStatementsUploaderComponent {
  private readonly dialogRef: BtwDialogRef = inject(BtwDialogRef);
  private readonly toastService: BtwToastService = inject(BtwToastService);
  
  //TEMP
  private readonly apiKey = inject(DIALOG_DATA) as Guid;

  private readonly bankStatementUploadApiService: BankStatementUploadApiService = inject(BankStatementUploadApiService);

  @ViewChild(BtwFileUploaderComponent, { static: true }) fileUploader: BtwFileUploaderComponent | undefined;
  
  uploadUrl: string = '';

  isFileUploaderValid: boolean = false;
  isUploadingInProgress: boolean = false;

  form: FormGroup<IBankStatementUploadForm> = new FormGroup<IBankStatementUploadForm>({
    companyName: new FormControl<string | null>(null, Validators.required)
  });

  handleFileUploaderValueChanged($event: any) {
    const { value } = $event;
    this.isFileUploaderValid = value.length > 0 && this.fileUploader?.validationErrors === null;
  }

  handleUpload(): void {
    if (!this.isValid()) {
      return;
    }

    this.isUploadingInProgress = true;

    const { companyName } = this.form.value;
    this.bankStatementUploadApiService.create(companyName!, this.apiKey!).pipe(
      tap((uploadReferenceId: Guid) => this.uploadUrl = `${this.bankStatementUploadApiService.getUploadUrl()}/${uploadReferenceId}`),
      switchMap((uploadReferenceId: Guid) => this.fileUploader!.startUpload$().pipe(
        map(data => ({ uploadReferenceId, data }))
      ))
    ).subscribe({
      next: result => {
        const { uploadReferenceId } = result;
        this.isUploadingInProgress = false; 
        
        this.dialogRef.close({
          uploadReferenceId,
          companyName
        });
      },
      error: err => {
        this.toastService.error('You do not have permission to perform this action!');
        this.isUploadingInProgress = false;
      }
    });
  }

  handleBeforeSend(data: any): void {
    data.request.open('POST', this.uploadUrl);
    data.request.setRequestHeader('apiKey', this.apiKey);
  }

  private isValid(): boolean {
    if (!this.form.valid || !this.isFileUploaderValid) {
      this.form.markAllAsTouched();

      if (!this.isFileUploaderValid) {
        this.toastService.error('Files are required!');
      }

      return false;
    }

    return true;
  }
}
